import { SIZE_PER_PAGE } from 'configs';
import {
  PlatformAdminOfferAvailabilityFilter,
  PlatformAdminOfferSortOption,
  PlatformAdminOfferStatusFilter,
  PlatformAdminOfferTypeFilter
} from 'hooks/admin/useSearchPlatformAdminOffers';
import { PlatformAdminRoleFilter } from 'hooks/admin/useSearchPlatformAdminUsers';
import { Dispatch, FC, SetStateAction, useCallback, useState } from 'react';
import { Container } from 'react-bootstrap';
import { Outlet } from 'react-router';
import './AdministrationPage.scss';

export type AdministrationUsersFilters = {
  searchInput: string;
  appliedSearch: string;
  roleFilter: PlatformAdminRoleFilter;
  page: number;
  pageSize: number;
};

export type AdministrationOffersFilters = {
  searchInput: string;
  appliedSearch: string;
  typeFilter: PlatformAdminOfferTypeFilter;
  statusFilter: PlatformAdminOfferStatusFilter;
  availabilityFilter: PlatformAdminOfferAvailabilityFilter;
  sortOption: PlatformAdminOfferSortOption;
  page: number;
  pageSize: number;
};

export type AdministrationOrganizationsFilters = {
  searchInput: string;
  appliedSearch: string;
  page: number;
  pageSize: number;
};

export type AdministrationOutletContext = {
  usersFilters: AdministrationUsersFilters;
  setUsersFilters: Dispatch<SetStateAction<AdministrationUsersFilters>>;
  organizationsFilters: AdministrationOrganizationsFilters;
  setOrganizationsFilters: Dispatch<SetStateAction<AdministrationOrganizationsFilters>>;
  offersFilters: AdministrationOffersFilters;
  setOffersFilters: Dispatch<SetStateAction<AdministrationOffersFilters>>;
};

const INITIAL_USERS_FILTERS: AdministrationUsersFilters = {
  searchInput: '',
  appliedSearch: '',
  roleFilter: 'all',
  page: 0,
  pageSize: SIZE_PER_PAGE
};

const INITIAL_OFFERS_FILTERS: AdministrationOffersFilters = {
  searchInput: '',
  appliedSearch: '',
  typeFilter: 'all',
  statusFilter: 'published',
  availabilityFilter: 'all',
  sortOption: 'name-asc',
  page: 0,
  pageSize: SIZE_PER_PAGE
};

const INITIAL_ORGANIZATIONS_FILTERS: AdministrationOrganizationsFilters = {
  searchInput: '',
  appliedSearch: '',
  page: 0,
  pageSize: SIZE_PER_PAGE
};

let persistedUsersFilters: AdministrationUsersFilters = INITIAL_USERS_FILTERS;
let persistedOffersFilters: AdministrationOffersFilters = INITIAL_OFFERS_FILTERS;
let persistedOrganizationsFilters: AdministrationOrganizationsFilters =
  INITIAL_ORGANIZATIONS_FILTERS;

export const AdministrationPage: FC = () => {
  const [usersFilters, setUsersFiltersState] = useState(
    () => persistedUsersFilters
  );
  const [offersFilters, setOffersFiltersState] = useState(
    () => persistedOffersFilters
  );
  const [organizationsFilters, setOrganizationsFiltersState] = useState(
    () => persistedOrganizationsFilters
  );

  const setUsersFilters: Dispatch<
    SetStateAction<AdministrationUsersFilters>
  > = useCallback((value) => {
    setUsersFiltersState((current) => {
      const next = typeof value === 'function' ? value(current) : value;
      persistedUsersFilters = next;
      return next;
    });
  }, []);

  const setOffersFilters: Dispatch<
    SetStateAction<AdministrationOffersFilters>
  > = useCallback((value) => {
    setOffersFiltersState((current) => {
      const next = typeof value === 'function' ? value(current) : value;
      persistedOffersFilters = next;
      return next;
    });
  }, []);

  const setOrganizationsFilters: Dispatch<
    SetStateAction<AdministrationOrganizationsFilters>
  > = useCallback((value) => {
    setOrganizationsFiltersState((current) => {
      const next = typeof value === 'function' ? value(current) : value;
      persistedOrganizationsFilters = next;
      return next;
    });
  }, []);

  return (
    <Container fluid className="administration-page">
      <Outlet
        context={
          {
            usersFilters,
            setUsersFilters,
            organizationsFilters,
            setOrganizationsFilters,
            offersFilters,
            setOffersFilters
          } satisfies AdministrationOutletContext
        }
      />
    </Container>
  );
};
